import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { APP_NAME, NAV_ITEMS, REAL_SNAPSHOT_DATA_LABEL } from '../../shared/constants.ts';
import { formatInteger, formatUtcStamp } from '../../shared/format.ts';
import { useDataset } from '../data/DatasetContext.tsx';

export function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { summary, refreshing, refreshError, refreshNotice, refreshData, session, logout } = useDataset();
  const real = summary?.dataLabel === REAL_SNAPSHOT_DATA_LABEL || summary?.usingSyntheticFixture === false;

  return (
    <div className="min-h-dvh bg-page text-text">
      <header className="border-b border-line bg-panel">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold tracking-[0.08em] uppercase">{APP_NAME.replace('Aviator', 'Aviator ')}</p>
            <nav className="mt-1 hidden flex-wrap gap-1 md:flex" aria-label="Primary">
              {NAV_ITEMS.map((item) => (
                <NavItem key={item.to} to={item.to} label={item.label} />
              ))}
            </nav>
          </div>
          <div className="flex items-start gap-3">
            <div className="text-right text-xs text-muted">
              <p className="font-semibold tracking-wide text-text">{summary ? (real ? 'DATA' : 'SYNTHETIC DATA') : 'Loading data…'}</p>
              <p>{summary ? `${formatInteger(summary.totalRounds)} rounds` : '—'}</p>
              <p>Updated {summary ? formatUtcStamp(summary.snapshotCreatedAtUtc ?? summary.maxDetectedAtUtc) : '—'}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <button
                type="button"
                className="rounded border border-line bg-page px-2.5 py-1 text-xs font-medium disabled:opacity-60"
                disabled={refreshing}
                onClick={() => void refreshData()}
              >
                {refreshing ? 'Refreshing...' : 'Refresh Data'}
              </button>
              {session.authRequired ? (
                <button type="button" className="text-[11px] text-muted" onClick={() => void logout()}>
                  Log out
                </button>
              ) : null}
            </div>
            <button
              type="button"
              className="rounded border border-line px-3 py-1 text-sm md:hidden"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-label="Toggle navigation"
            >
              Menu
            </button>
          </div>
        </div>
        {refreshError ? (
          <p className="border-t border-warn/40 px-4 py-2 text-xs text-warn" role="alert">
            {refreshError}
          </p>
        ) : null}
        {refreshNotice ? (
          <p className="border-t border-line px-4 py-2 text-xs text-muted" role="status">
            {refreshNotice}
          </p>
        ) : null}
        {menuOpen ? (
          <nav className="flex flex-col border-t border-line px-4 py-2 md:hidden" aria-label="Primary">
            {NAV_ITEMS.map((item) => (
              <NavItem key={item.to} to={item.to} label={item.label} onClick={() => setMenuOpen(false)} />
            ))}
          </nav>
        ) : null}
      </header>
      <main className="mx-auto max-w-7xl px-4 py-5">
        <Outlet />
      </main>
    </div>
  );
}

function NavItem(props: { to: string; label: string; onClick?: () => void }) {
  return (
    <NavLink
      to={props.to}
      end={props.to === '/'}
      onClick={props.onClick}
      className={({ isActive }) =>
        `rounded px-2.5 py-1 text-sm ${
          isActive ? 'bg-soft font-medium text-ink' : 'text-muted hover:text-text'
        }`
      }
    >
      {props.label}
    </NavLink>
  );
}
